import type { ReactNode } from "react";
import { RAIL_ICON_LABELS, SPINE_ICON_LABELS } from "./iconLabels";
import "./Icon.css";

type IconProps = {
  size?: number;
  title?: string;
  decorative?: boolean;
  className?: string;
};

function BaseIcon({
  size = 20,
  title,
  decorative = true,
  className,
  children,
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      className={["e3-icon", className].filter(Boolean).join(" ")}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : title}
    >
      {!decorative && title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

/** Line-art glyph bodies keyed by meaning. Every rail label resolves to one. */
const GLYPHS: Record<string, ReactNode> = {
  // Operating spine
  bound: (
    <>
      <circle cx="12" cy="12" r="7.25" />
      <circle cx="12" cy="12" r="2.25" fill="currentColor" stroke="none" />
    </>
  ),
  see: (
    <>
      {[5.5, 12, 18.5].map((x) =>
        [5.5, 12, 18.5].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="1.3" fill="currentColor" stroke="none" />
        )),
      )}
    </>
  ),
  choose: (
    <>
      <circle cx="12" cy="12" r="7.25" />
      <circle cx="12" cy="12" r="3.5" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  shift: (
    <>
      <path d="M5 15c3-1 5-5 6-9 1 4 3 8 6 9" />
      <path d="M14 8.5l4 0.5-1 3.8" />
    </>
  ),
  learn: (
    <>
      <path d="M6.5 10.5A5.5 5.5 0 0 1 16 7" />
      <path d="M17.5 13.5A5.5 5.5 0 0 1 8 17" />
      <path d="M15.6 4.4L16.5 7.6l-3.2-.5" />
      <path d="M8.4 19.6L7.5 16.4l3.2.5" />
    </>
  ),

  // Field
  orientation: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M9 15l3-6 3 6-3-1.6z" />
    </>
  ),
  verdict: (
    <>
      <path d="M12 5v13" />
      <path d="M5.5 9h13" />
      <path d="M5.5 9l-2 4.5h4z" />
      <path d="M18.5 9l-2 4.5h4z" />
    </>
  ),
  fourFields: (
    <>
      <rect x="4.5" y="4.5" width="6" height="6" rx="1" />
      <rect x="13.5" y="4.5" width="6" height="6" rx="1" />
      <rect x="4.5" y="13.5" width="6" height="6" rx="1" />
      <rect x="13.5" y="13.5" width="6" height="6" rx="1" />
    </>
  ),

  // Stellar
  proposition: (
    <>
      <path d="M5 19h14" />
      <path d="M12 16V6" />
      <path d="M8.5 9.5L12 6l3.5 3.5" />
    </>
  ),
  lexicon: (
    <>
      <path d="M12 6.5C10 5 7.5 5 5.5 6v12c2-1 4.5-1 6.5.5" />
      <path d="M12 6.5c2-1.5 4.5-1.5 6.5-.5v12c-2-1-4.5-1-6.5.5" />
      <path d="M12 6.5v12" />
    </>
  ),
  recognition: (
    <>
      <path d="M3.5 12S6.8 6.5 12 6.5 20.5 12 20.5 12 17.2 17.5 12 17.5 3.5 12 3.5 12z" />
      <circle cx="12" cy="12" r="2.4" />
    </>
  ),
  giftLimit: (
    <>
      <rect x="5" y="8.5" width="14" height="10" rx="1.5" />
      <path d="M5 12h14" />
      <path d="M12 8.5v10" />
      <path d="M8.5 8.5a2.2 2.2 0 0 1 3.5-2.6 2.2 2.2 0 0 1 3.5 2.6" />
    </>
  ),

  // Earth 2.0
  declaration: (
    <>
      <path d="M7 20V5" />
      <path d="M7 5.5c3.5-1.6 6.5 1.6 10 0v7c-3.5 1.6-6.5-1.6-10 0z" />
    </>
  ),
  mandate: (
    <>
      <circle cx="12" cy="9.5" r="4.5" />
      <path d="M9.5 13.5L8.5 20l3.5-2 3.5 2-1-6.5" />
    </>
  ),
  room: (
    <>
      <rect x="4.5" y="9.5" width="15" height="6" rx="2.5" />
      <circle cx="7.5" cy="6.5" r="1.4" />
      <circle cx="12" cy="6.5" r="1.4" />
      <circle cx="16.5" cy="6.5" r="1.4" />
      <path d="M7.5 15.5v3M16.5 15.5v3" />
    </>
  ),
  caseMethod: (
    <>
      <path d="M4.5 8.5l7.5-3.5 7.5 3.5-7.5 3.5z" />
      <path d="M4.5 12.5l7.5 3.5 7.5-3.5" />
      <path d="M4.5 16l7.5 3.5 7.5-3.5" />
    </>
  ),
  threshold: (
    <>
      <path d="M6 19V10a6 6 0 0 1 12 0v9" />
      <path d="M4 19h16" />
      <path d="M14.5 13.5h.01" />
    </>
  ),
  assessment: (
    <>
      <path d="M5 7h9M5 12h9M5 17h5" />
      <path d="M15.5 15.5l2 2 3-3.5" />
    </>
  ),
  refusals: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M7 17L17 7" />
    </>
  ),

  // I Eat the Stars
  contrast: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M12 4.5a7.5 7.5 0 0 0 0 15z" fill="currentColor" stroke="none" />
    </>
  ),
  sharedGround: (
    <>
      <path d="M3.5 16.5h17" />
      <path d="M7 16.5a5 5 0 0 1 5-5 5 5 0 0 1 5 5" />
      <circle cx="9" cy="8.5" r="1.4" />
      <circle cx="15" cy="8.5" r="1.4" />
    </>
  ),
  correction: (
    <>
      <path d="M5 13a6 6 0 0 1 12 0v3" />
      <path d="M14 13.5L17 16.5l3-3" />
      <path d="M5 13V9.5" />
    </>
  ),
  realityCheck: (
    <>
      <circle cx="10.5" cy="10.5" r="5.5" />
      <path d="M14.6 14.6L20 20" />
      <path d="M8.5 10.5l1.6 1.6 2.9-3.2" />
    </>
  ),

  // Earth 3.0
  premise: (
    <>
      <path d="M12 19v-6" />
      <path d="M12 13c-3.5 0-5.5-2-5.5-5 3.5 0 5.5 2 5.5 5z" />
      <path d="M12 13c3.5 0 5.5-2 5.5-5-3.5 0-5.5 2-5.5 5z" />
    </>
  ),
  architecture: (
    <>
      <rect x="4.5" y="4.5" width="15" height="15" rx="1.5" />
      <path d="M4.5 12h15M12 4.5v15" />
    </>
  ),
  commons: (
    <>
      <circle cx="12" cy="7" r="2.3" />
      <circle cx="6.8" cy="16" r="2.3" />
      <circle cx="17.2" cy="16" r="2.3" />
      <path d="M10.4 8.8L8.4 14M13.6 8.8L15.6 14M9.1 16h5.8" />
    </>
  ),
  decisions: (
    <>
      <path d="M12 19v-5" />
      <path d="M12 14L6.5 9.5V6" />
      <path d="M12 14l5.5-4.5V6" />
      <circle cx="6.5" cy="5" r="1.4" />
      <circle cx="17.5" cy="5" r="1.4" />
      <circle cx="12" cy="20" r="1.4" />
    </>
  ),
  proof: (
    <>
      <rect x="4" y="8.5" width="16" height="7" rx="1.5" />
      <path d="M8 8.5v3M12 8.5v4M16 8.5v3" />
    </>
  ),

  // Practice
  position: (
    <>
      <path d="M12 20s6-5.4 6-9.6A6 6 0 0 0 6 10.4C6 14.6 12 20 12 20z" />
      <circle cx="12" cy="10.2" r="2.2" />
    </>
  ),
  offers: (
    <>
      <rect x="4.5" y="7" width="11" height="9" rx="1.5" />
      <path d="M7.5 4.5h11a1.5 1.5 0 0 1 1.5 1.5v9" />
      <path d="M7.5 11.5h5" />
    </>
  ),
  fit: (
    <>
      <path d="M10.5 5H6.5A1.5 1.5 0 0 0 5 6.5v4a2 2 0 0 1 0 3v4A1.5 1.5 0 0 0 6.5 19h4" />
      <path d="M13.5 19h4a1.5 1.5 0 0 0 1.5-1.5v-4a2 2 0 0 1 0-3v-4A1.5 1.5 0 0 0 17.5 5h-4" />
    </>
  ),
  narrative: (
    <>
      <path d="M4.5 6.5h15v9h-9L6 19v-3.5H4.5z" />
      <path d="M8 10h8M8 12.8h5" />
    </>
  ),
  ninetyDays: (
    <>
      <circle cx="12" cy="12.5" r="7" />
      <path d="M12 8.8v3.7l2.8 1.8" />
      <path d="M9 4h6" />
    </>
  ),
  message: (
    <>
      <rect x="4" y="6.5" width="16" height="11" rx="1.5" />
      <path d="M4.5 7.5L12 13l7.5-5.5" />
    </>
  ),
  naming: (
    <>
      <path d="M4.5 10.5l6-6h7a2 2 0 0 1 2 2v7l-6 6z" />
      <circle cx="15.5" cy="8.5" r="1.5" />
    </>
  ),

  // Evidence
  evidenceGrammar: (
    <>
      <path d="M9.5 5c-2 0-2.5 1.4-2.5 3.2 0 1.6-.6 2.6-2 3.3 1.4.7 2 1.7 2 3.3 0 1.8.5 3.2 2.5 3.2" />
      <path d="M14.5 5c2 0 2.5 1.4 2.5 3.2 0 1.6.6 2.6 2 3.3-1.4.7-2 1.7-2 3.3 0 1.8-.5 3.2-2.5 3.2" />
    </>
  ),
  coreTexts: (
    <>
      <rect x="5" y="4.5" width="10" height="13" rx="1.2" />
      <path d="M8 4.5v13" />
      <path d="M17 7v11.5a1.5 1.5 0 0 1-1.5 1.5H7" />
    </>
  ),
  planetary: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M4.6 9.6h14.8M4.6 14.4h14.8" />
      <path d="M12 4.5c-3 3-3 12 0 15 3-3 3-12 0-15z" />
    </>
  ),
  lineages: (
    <>
      <path d="M4.5 19V5" />
      <path d="M4.5 8h6a3 3 0 0 1 3 3v0" />
      <path d="M4.5 14h9a3 3 0 0 1 3 3v0" />
      <circle cx="14.5" cy="11" r="1.4" />
      <circle cx="17.5" cy="17" r="1.4" />
    </>
  ),
  rights: (
    <>
      <path d="M12 4.5l6.5 2.4v5.6c0 3.6-2.7 5.9-6.5 7-3.8-1.1-6.5-3.4-6.5-7V6.9z" />
      <path d="M9.5 12l2 2 3.5-4" />
    </>
  ),
  openQuestions: (
    <>
      <path d="M8.5 8.8a3.5 3.5 0 1 1 4.9 3.2c-1 .5-1.4 1.2-1.4 2.2" />
      <circle cx="12" cy="18" r="1.2" fill="currentColor" stroke="none" />
      <path d="M4.5 12a7.5 7.5 0 0 1 3-6" />
      <path d="M19.5 12a7.5 7.5 0 0 0-3-6" />
    </>
  ),

  // Navigation actions
  menu: <path d="M5 8h14M5 12h14M5 16h14" />,
  close: <path d="M7 7l10 10M17 7L7 17" />,
};

/** Every rail label used by the seven canonical views maps to a glyph. */
const RAIL_GLYPHS: Record<string, keyof typeof GLYPHS> = {
  // Field
  Orientation: "orientation",
  "Research verdict": "verdict",
  "Four fields": "fourFields",
  // Stellar
  Proposition: "proposition",
  Lexicon: "lexicon",
  Recognition: "recognition",
  "Gift & limit": "giftLimit",
  // Earth 2.0
  Declaration: "declaration",
  Mandate: "mandate",
  "The room": "room",
  "Case method": "caseMethod",
  Threshold: "threshold",
  Assessment: "assessment",
  Refusals: "refusals",
  // I Eat the Stars
  Contrast: "contrast",
  "Shared ground": "sharedGround",
  Correction: "correction",
  "Reality check": "realityCheck",
  // Earth 3.0
  Premise: "premise",
  Architecture: "architecture",
  Commons: "commons",
  Decisions: "decisions",
  Proof: "proof",
  // Practice
  Position: "position",
  Offers: "offers",
  Fit: "fit",
  Narrative: "narrative",
  "90 days": "ninetyDays",
  Message: "message",
  Naming: "naming",
  // Evidence
  "Evidence grammar": "evidenceGrammar",
  "Core texts": "coreTexts",
  "Planetary condition": "planetary",
  "Transition lineages": "lineages",
  "Rights & legitimacy": "rights",
  "Open questions": "openQuestions",
};

const SPINE_GLYPHS: Record<string, keyof typeof GLYPHS> = {
  Bound: "bound",
  See: "see",
  Choose: "choose",
  Shift: "shift",
  Learn: "learn",
};

for (const label of RAIL_ICON_LABELS) {
  if (!RAIL_GLYPHS[label]) {
    throw new Error(`RAIL_GLYPHS missing label from RAIL_ICON_LABELS: ${label}`);
  }
}
for (const label of SPINE_ICON_LABELS) {
  if (!SPINE_GLYPHS[label]) {
    throw new Error(`SPINE_GLYPHS missing label from SPINE_ICON_LABELS: ${label}`);
  }
}

export function RailIcon({ label, ...props }: IconProps & { label: string }) {
  const key = RAIL_GLYPHS[label];
  if (!key) {
    throw new Error(`Missing rail icon for label: ${label}`);
  }
  return (
    <BaseIcon {...props} title={props.title ?? label}>
      {GLYPHS[key]}
    </BaseIcon>
  );
}

export function SpineIcon({ label, ...props }: IconProps & { label: string }) {
  const key = SPINE_GLYPHS[label];
  if (!key) {
    throw new Error(`Missing spine icon for label: ${label}`);
  }
  return (
    <BaseIcon {...props} title={props.title ?? label}>
      {GLYPHS[key]}
    </BaseIcon>
  );
}

export function IconMenu(props: IconProps) {
  return (
    <BaseIcon {...props} title={props.title ?? "Menu"} decorative={props.decorative ?? false}>
      {GLYPHS.menu}
    </BaseIcon>
  );
}

export function IconClose(props: IconProps) {
  return (
    <BaseIcon {...props} title={props.title ?? "Close"} decorative={props.decorative ?? false}>
      {GLYPHS.close}
    </BaseIcon>
  );
}
